import {
  useI18n
} from "@/composables/useI18n";
import type { ImportResult } from "@/scripts/Common/types";
import type {Ref} from "vue";
import {useGlobalSnackbar} from "@/composables/useGlobalSnackbar";
import type {SnackbarStatus} from "@/components/base/globalSnackbar/types";

const useSnackbar = (_snackbarRef?: Ref) => {
  const {
    t,
  } = useI18n()

  const { show } = useGlobalSnackbar();

  type OperationActionMap = (operationType: string) => string;

  interface SnackbarOptions {
    statusTextSuccessI18nKey?: string;
    statusTextFailedItemsI18nKey?: string;
  }

  const actionInterpolation = (count: number, action: string) => ({ count, action }) as Record<string, unknown>;

  const showSnackbar = (
    {
      status = undefined,
      statusText = undefined
    }: {
      status?: string,
      statusText?: string[]
    }
  ) => {
    show(
      (status ? status : 'success') as SnackbarStatus,
      statusText ? statusText : [t('ErrorItemDialog.success')]
    );
  };

  /**
   * Shows a snackbar with the response of an operation. Only works with a OperationResultDto<T> of the backend.
   * @param response - response object. Must have the following properties: successfulItems, failedItems, operationType
   * @param callback - function that maps operationType of the response to a string that describes the action
   */
  const showSnackbarResponse = (
    response: Record<any, any>,
    callback: OperationActionMap,
    options: SnackbarOptions = {}
  ) => {
    const {
      statusTextSuccessI18nKey = 'ErrorItemDialog.successful_items',
      statusTextFailedItemsI18nKey = 'ErrorItemDialog.failed_items',
    } = options;

    const { successfulItems, failedItems, operationType } = response;

    const action = callback(operationType);

    const statusText = [];
    let status = 'success';

    if (successfulItems.length > 0) {
      const count = successfulItems.length;
      statusText.push(t(statusTextSuccessI18nKey, actionInterpolation(count, action)) as string);
    }

    if (failedItems.length > 0) {
      status = 'warning';
      const count = failedItems.length;
      statusText.push(t(statusTextFailedItemsI18nKey, actionInterpolation(count, action)) as string);
    }

    showSnackbar({status, statusText})
  }

  const showSnackbarForImportResult = (
    result: ImportResult,
    options: SnackbarOptions = {}
  ) => {
    const {
      statusTextSuccessI18nKey = 'successful_items',
      statusTextFailedItemsI18nKey = 'failed_items',
    } = options;

    const statusText: string[] = [];
    const status = result.failedImports > 0 ? 'warning' : 'success';

    if (result.successfulImports > 0) {
      statusText.push(t(statusTextSuccessI18nKey, result.successfulImports) as string);
    }

    if (result.failedImports > 0) {
      statusText.push(t(statusTextFailedItemsI18nKey, result.failedImports) as string);
    }

    showSnackbar({status, statusText})
  }

  return {
    showSnackbarResponse,
    showSnackbarForImportResult,
    showSnackbar,
  }

}


export {
  useSnackbar
}
